/* ==========================================================
   Scent Quiz — state machine + recommender
   --------------------------------------------------
   Reads product data from <script data-quiz-products> JSON,
   collects user answers across 7 steps via [data-axes] deltas,
   scores every product, and reveals one main match + 3 bonus
   recommendations using the pre-rendered <template> pool.

   No framework, no fetches except cart/add.js (mirrors the
   pattern used by assets/scent-card.js).
   ========================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-quiz]');
  if (!root) return;

  var blob = root.querySelector('[data-quiz-products]');
  var cardPool = root.querySelector('[data-quiz-card-pool]');
  if (!blob) return;

  var products = [];
  try {
    products = JSON.parse(blob.textContent.trim() || '[]');
  } catch (e) {
    console.error('[scent-quiz] product blob parse error', e);
    return;
  }

  // Steps that are full-question screens (used for the progressbar count)
  var QUESTION_STEPS = ['1', '2', '3', '4', '5', '6', '7'];
  var MAX_NOTES = 4;

  // Scoring weights (see plan §4)
  var W_FAMILY = 30;
  var W_WARMTH = 8;
  var W_FRESH = 8;
  var W_INT = 10;
  var W_LONG = 8;
  var W_NOTE = 3;
  var W_NOTE_CAP = 18;

  // ---------- state ----------
  var u = freshAxes();
  var axisHistory = {}; // { step: deltaSnapshot } so back can revert

  function freshAxes() {
    return {
      family: { floral: 0, oriental: 0, fresh: 0, woody: 0, gourmand: 0 },
      warmth: 0,
      freshness: 0,
      intensity: 3,
      longevity: 3,
      noteAffinity: {},
      noteTokens: [],     // tokens the user selected at Q6 (for filtering)
      noteMatchMode: ''   // '', 'strict', 'relaxed', 'none' — set during rank()
    };
  }

  // Does a product's note bank contain a given token? Substring both ways
  // so "bergamot" matches "bergamote" and vice-versa.
  function productHasNote(p, token) {
    var bank = (p.top_notes || []).concat(p.middle_notes || [], p.bottom_notes || []);
    return bank.some(function (n) {
      if (!n) return false;
      return n.indexOf(token) !== -1 || token.indexOf(n) !== -1;
    });
  }

  function countNoteMatches(p) {
    if (!u.noteTokens || !u.noteTokens.length) return 0;
    var n = 0;
    for (var i = 0; i < u.noteTokens.length; i++) {
      if (productHasNote(p, u.noteTokens[i])) n++;
    }
    return n;
  }

  // ---------- step machine ----------
  var stage = root.querySelector('.scent-quiz__stage');
  var steps = root.querySelectorAll('.scent-quiz__step');
  var progressDots = root.querySelectorAll('[data-quiz-progressbar] .dot');

  function show(stepName) {
    var name = String(stepName);
    steps.forEach(function (s) {
      s.classList.toggle('is-active', s.dataset.step === name);
    });
    updateProgressbar(name);
    // Scroll the section into view if needed (mirrors the original UX)
    var top = root.getBoundingClientRect().top + window.scrollY - 60;
    if (window.scrollY > top + 120) {
      window.scrollTo({ top: top, behavior: 'smooth' });
    }
  }

  function updateProgressbar(stepName) {
    if (!progressDots || !progressDots.length) return;
    var idx = QUESTION_STEPS.indexOf(stepName);
    progressDots.forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === idx);
      dot.classList.toggle('is-done', idx > -1 && i < idx);
    });
  }

  function currentStepName() {
    var active = root.querySelector('.scent-quiz__step.is-active');
    return active ? active.dataset.step : '0';
  }

  // ---------- axis math ----------
  // Convention for the JSON in [data-axes]:
  //   family: { floral:+1, oriental:+2, ... } — additive per family
  //   warmth, freshness, intensity, longevity — additive deltas
  //   intensityAbs, longevityAbs — absolute SET (replaces current value)
  //
  // applyAxes mutates `u` and returns a snapshot of what changed so the
  // back button can revert it cleanly.
  function applyAxes(d) {
    if (!d) return null;
    var snap = {
      family: {},
      warmth: 0, freshness: 0,
      intensityDelta: 0, longevityDelta: 0,
      prevIntensity: u.intensity, prevLongevity: u.longevity,
      intensitySet: false, longevitySet: false
    };
    if (d.family) {
      Object.keys(d.family).forEach(function (k) {
        if (u.family[k] == null) return;
        u.family[k] += d.family[k];
        snap.family[k] = d.family[k];
      });
    }
    if (typeof d.warmth === 'number')    { u.warmth    += d.warmth;    snap.warmth    = d.warmth; }
    if (typeof d.freshness === 'number') { u.freshness += d.freshness; snap.freshness = d.freshness; }
    if (typeof d.intensity === 'number') { u.intensity += d.intensity; snap.intensityDelta = d.intensity; }
    if (typeof d.longevity === 'number') { u.longevity += d.longevity; snap.longevityDelta = d.longevity; }
    if (typeof d.intensityAbs === 'number') { u.intensity = d.intensityAbs; snap.intensitySet = true; }
    if (typeof d.longevityAbs === 'number') { u.longevity = d.longevityAbs; snap.longevitySet = true; }
    return snap;
  }

  // Reverts a snapshot from applyAxes
  function revertAxes(snap) {
    if (!snap) return;
    Object.keys(snap.family || {}).forEach(function (k) {
      if (u.family[k] != null) u.family[k] -= snap.family[k];
    });
    if (snap.warmth)         u.warmth    -= snap.warmth;
    if (snap.freshness)      u.freshness -= snap.freshness;
    if (snap.intensityDelta) u.intensity -= snap.intensityDelta;
    if (snap.longevityDelta) u.longevity -= snap.longevityDelta;
    // Absolute sets restore the previous value
    if (snap.intensitySet) u.intensity = snap.prevIntensity;
    if (snap.longevitySet) u.longevity = snap.prevLongevity;
  }

  // ---------- Q6 multi-select (notes) ----------
  function selectedNotePills() {
    return root.querySelectorAll('[data-quiz-note-toggle].is-selected');
  }

  function togglePill(pill) {
    var token = pill.getAttribute('data-token');
    if (!token) return;
    var isSelected = pill.classList.contains('is-selected');
    if (!isSelected && selectedNotePills().length >= MAX_NOTES) {
      // Already at max — shake the counter AND the pill the user tried to add
      pill.classList.add('is-shake');
      window.setTimeout(function () { pill.classList.remove('is-shake'); }, 380);
      var counter = root.querySelector('[data-quiz-note-counter]');
      if (counter) {
        counter.classList.add('is-shake');
        window.setTimeout(function () { counter.classList.remove('is-shake'); }, 380);
      }
      return;
    }
    pill.classList.toggle('is-selected');
    updateNoteCounter();
  }

  function updateNoteCounter() {
    var counter = root.querySelector('[data-quiz-note-counter]');
    if (!counter) return;
    var n = selectedNotePills().length;
    counter.textContent = n + ' / ' + MAX_NOTES;
    counter.classList.toggle('is-full', n >= MAX_NOTES);
  }

  function commitMultiSelect() {
    // Build a single combined delta from currently selected pills, then apply.
    var combined = { family: {}, warmth: 0, freshness: 0 };
    var selected = selectedNotePills();
    u.noteTokens = [];
    selected.forEach(function (pill) {
      var token = pill.getAttribute('data-token');
      if (token) {
        u.noteAffinity[token] = 1;
        if (u.noteTokens.indexOf(token) === -1) u.noteTokens.push(token);
      }
      var raw = pill.getAttribute('data-axes');
      if (!raw) return;
      try {
        var d = JSON.parse(raw);
        if (d.family) Object.keys(d.family).forEach(function (k) {
          combined.family[k] = (combined.family[k] || 0) + d.family[k];
        });
        if (typeof d.warmth === 'number')    combined.warmth    += d.warmth;
        if (typeof d.freshness === 'number') combined.freshness += d.freshness;
      } catch (e) { /* ignore */ }
    });
    return applyAxes(combined);
  }

  function clearMultiSelect() {
    root.querySelectorAll('[data-quiz-note-toggle]').forEach(function (p) {
      p.classList.remove('is-selected');
    });
    updateNoteCounter();
  }

  // ---------- scoring ----------
  function familyPref() {
    var fam = u.family;
    var max = Math.max.apply(null, Object.keys(fam).map(function (k) { return fam[k]; }));
    if (!max || max <= 0) max = 1;
    var out = {};
    Object.keys(fam).forEach(function (k) { out[k] = fam[k] > 0 ? fam[k] / max : 0; });
    return out;
  }

  function scoreProduct(p, fp) {
    var s = 0;

    // 1. Family alignment (largest weight)
    if (p.family && fp[p.family] != null) {
      s += W_FAMILY * fp[p.family]; // 0..30
    }

    // 2. Warmth alignment
    if (p.warmth >= 0) {
      var pw = (p.warmth - 2.5) * 2;          // map 0..5 → -5..+5
      var dw = Math.abs(pw - u.warmth);       // 0..10
      s += W_WARMTH * Math.max(0, 1 - dw / 10);
    }

    // 3. Freshness alignment
    if (p.freshness >= 0) {
      var pf = (p.freshness - 2.5) * 2;
      var df = Math.abs(pf - u.freshness);
      s += W_FRESH * Math.max(0, 1 - df / 10);
    }

    // 4. Intensity alignment
    if (p.intensity >= 0) {
      var di = Math.abs(p.intensity - u.intensity); // 0..5
      s += W_INT * Math.max(0, 1 - di / 5);
    }

    // 5. Longevity alignment
    if (p.longevity >= 0) {
      var dl = Math.abs(p.longevity - u.longevity);
      s += W_LONG * Math.max(0, 1 - dl / 5);
    }

    // 6. Note keyword affinity (multi-select bonus)
    var noteBank = (p.top_notes || []).concat(p.middle_notes || [], p.bottom_notes || []);
    var noteScore = 0;
    Object.keys(u.noteAffinity).forEach(function (token) {
      var hit = noteBank.some(function (n) {
        if (!n) return false;
        return n.indexOf(token) !== -1 || token.indexOf(n) !== -1;
      });
      if (hit) noteScore += W_NOTE;
    });
    s += Math.min(noteScore, W_NOTE_CAP);

    // Tiny availability nudge
    if (p.available === false) s -= 2;

    return s;
  }

  function rank(fp) {
    var hasNotes = u.noteTokens && u.noteTokens.length > 0;

    var scored = products.map(function (p) {
      return {
        p: p,
        s: scoreProduct(p, fp),
        matches: hasNotes ? countNoteMatches(p) : 0
      };
    });

    // ---- Honesty filter -----------------------------------------------
    // If the user picked notes at Q6, the result MUST contain at least one
    // of those notes — otherwise the answer is unfaithful to the quiz.
    // Two-tier strategy: try strict (>=50%) first, fall back to >=1, then
    // fall back to all products (rare: catalog has zero overlap).
    if (hasNotes) {
      u.noteMatchMode = 'strict';
      var strictThreshold = Math.max(1, Math.ceil(u.noteTokens.length / 2));
      var strict = scored.filter(function (x) { return x.matches >= strictThreshold; });
      if (strict.length > 0) {
        scored = strict;
      } else {
        u.noteMatchMode = 'relaxed';
        var relaxed = scored.filter(function (x) { return x.matches >= 1; });
        if (relaxed.length > 0) {
          scored = relaxed;
        } else {
          u.noteMatchMode = 'none';
          // catalog has no overlap with the user's notes; keep all but flag
        }
      }
    } else {
      u.noteMatchMode = '';
    }

    return scored.sort(function (a, b) {
      // When notes were chosen, match count is the dominant signal — more
      // matching notes wins, period. Score breaks ties among equal counts.
      if (hasNotes && b.matches !== a.matches) return b.matches - a.matches;
      if (b.s !== a.s) return b.s - a.s;
      var bi = b.p.intensity || 0, ai = a.p.intensity || 0;
      if (bi !== ai) return bi - ai;
      var bl = b.p.longevity || 0, al = a.p.longevity || 0;
      if (bl !== al) return bl - al;
      return (a.p.id || 0) - (b.p.id || 0);
    });
  }

  // Returns an array of { p, matches } envelopes so the renderer can show
  // each card's note-match count alongside the card.
  function pickBonuses(rest, main, n) {
    var out = [];
    var usedFamilies = {};
    if (main && main.family) usedFamilies[main.family] = true;
    // Pass 1: prefer different families (rest is already sorted by relevance)
    for (var i = 0; i < rest.length && out.length < n; i++) {
      var entry = rest[i];
      var p = entry.p;
      if (!p || p.id === main.id) continue;
      if (p.family && !usedFamilies[p.family]) {
        out.push(entry);
        usedFamilies[p.family] = true;
      }
    }
    // Pass 2: top up from remaining
    for (var j = 0; j < rest.length && out.length < n; j++) {
      var entry2 = rest[j];
      var q = entry2.p;
      if (!q || q.id === main.id) continue;
      if (out.some(function (e) { return e.p.id === q.id; })) continue;
      out.push(entry2);
    }
    return out;
  }

  // ---------- rendering ----------
  function computeAndReveal() {
    if (!products.length) {
      console.warn('[scent-quiz] no products in source collection');
      show('result');
      return;
    }
    var fp = familyPref();
    var ranked = rank(fp);
    var mainEntry = ranked[0];
    var main = mainEntry.p;
    var bonuses = pickBonuses(ranked.slice(1), main, 3);
    renderHero(main, fp, mainEntry.matches || 0);
    renderBonuses(bonuses);
    show('result');
  }

  function renderHero(p, fp, matchCount) {
    var hero = root.querySelector('[data-quiz-hero]');
    if (!hero) return;
    hero.hidden = false;

    var imgEl = hero.querySelector('[data-hero-img]');
    if (imgEl) {
      imgEl.src = p.img || '';
      imgEl.alt = p.title || '';
    }
    setText(hero, '[data-hero-title]', p.title || '');
    setText(hero, '[data-hero-price]', p.price || '');
    setText(hero, '[data-hero-family]', (p.family || '').toUpperCase());

    setHref(hero, '[data-hero-link]', p.url);
    setHref(hero, '[data-hero-pdp]', p.url);

    // Match badge — appears only when notes were selected at Q6, and reports
    // honestly: "3 / 4 notes you love" or, in the worst case, a disclaimer
    // that the catalog has no overlap with the picks.
    var matchEl = hero.querySelector('[data-hero-match]');
    if (matchEl) {
      var selectedCount = (u.noteTokens || []).length;
      if (selectedCount === 0) {
        matchEl.hidden = true;
        matchEl.textContent = '';
        matchEl.classList.remove('is-warn');
      } else if (u.noteMatchMode === 'none') {
        matchEl.hidden = false;
        matchEl.classList.add('is-warn');
        matchEl.textContent =
          'Aucun parfum du catalogue ne porte exactement vos notes — voici le plus proche par humeur.';
      } else {
        matchEl.hidden = false;
        matchEl.classList.remove('is-warn');
        matchEl.textContent =
          matchCount + ' / ' + selectedCount + ' notes que vous aimez sont présentes';
      }
    }

    // Notes pyramid — render three labeled groups (Tête / Cœur / Fond)
    // each with its own chip row. Empty layers are omitted so the section
    // stays compact for products without bottom notes, etc.
    var notesEl = hero.querySelector('[data-hero-notes]');
    if (notesEl) {
      notesEl.innerHTML = '';
      var tokens = u.noteTokens || [];
      var layers = [
        { key: 'top',    label: 'Tête',  names: p.top_notes    || [], colors: p.top_colors    || [] },
        { key: 'middle', label: 'Cœur',  names: p.middle_notes || [], colors: p.middle_colors || [] },
        { key: 'bottom', label: 'Fond',  names: p.bottom_notes || [], colors: p.bottom_colors || [] }
      ];
      layers.forEach(function (layer) {
        if (!layer.names.length) return;

        var group = document.createElement('div');
        group.className = 'scent-quiz__hero-notes-group';
        group.setAttribute('data-layer', layer.key);

        var label = document.createElement('p');
        label.className = 'scent-quiz__hero-notes-label';
        label.textContent = layer.label;
        group.appendChild(label);

        var row = document.createElement('div');
        row.className = 'scent-quiz__hero-notes-row';
        layer.names.forEach(function (name, i) {
          var lname = (name || '').toLowerCase();
          var isMatched = tokens.some(function (t) {
            return lname.indexOf(t) !== -1 || t.indexOf(lname) !== -1;
          });
          var pill = document.createElement('span');
          pill.className = 'scent-quiz__note-chip' + (isMatched ? ' is-matched' : '');
          var dot = document.createElement('span');
          dot.className = 'scent-quiz__note-chip-dot';
          dot.style.background = layer.colors[i] || 'rgb(var(--color-button))';
          pill.appendChild(dot);
          pill.appendChild(document.createTextNode(' ' + capitalize(name)));
          row.appendChild(pill);
        });
        group.appendChild(row);

        notesEl.appendChild(group);
      });
    }

    // "Why this matches" bullets
    var whyEl = hero.querySelector('[data-hero-why]');
    if (whyEl) {
      whyEl.innerHTML = '';
      buildWhyBullets(p, fp).slice(0, 3).forEach(function (text) {
        var li = document.createElement('li');
        li.textContent = text;
        whyEl.appendChild(li);
      });
    }

    // ATC button
    var atcBtn = hero.querySelector('[data-hero-atc]');
    if (atcBtn) {
      atcBtn.setAttribute('data-variant-id', p.variant_id);
      atcBtn.disabled = p.available === false;
      atcBtn.classList.remove('is-loading', 'is-success', 'is-error');
    }
  }

  function buildWhyBullets(p, fp) {
    var bullets = [];
    var familyLabels = {
      floral: 'florale', oriental: 'orientale', fresh: 'fraîche',
      woody: 'boisée', gourmand: 'gourmande'
    };
    if (p.family && fp[p.family] != null && fp[p.family] >= 0.8) {
      bullets.push('Famille ' + (familyLabels[p.family] || p.family) + ', votre choix dominant.');
    }
    if (p.intensity >= 4 && u.intensity >= 4) {
      bullets.push('Sillage puissant, à la hauteur de votre signature.');
    } else if (p.intensity >= 0 && p.intensity <= 2 && u.intensity <= 2) {
      bullets.push('Sillage discret, pour les proches uniquement.');
    }
    if (p.longevity >= 4 && u.longevity >= 4) {
      bullets.push('Tient plus de 8 heures sur la peau.');
    }
    var matched = [];
    var bank = (p.top_notes || []).concat(p.middle_notes || [], p.bottom_notes || []);
    Object.keys(u.noteAffinity).forEach(function (token) {
      if (bank.some(function (n) { return n && (n.indexOf(token) !== -1 || token.indexOf(n) !== -1); })) {
        matched.push(capitalize(token));
      }
    });
    if (matched.length) {
      bullets.push('Notes que vous adorez : ' + matched.slice(0, 3).join(', ') + '.');
    }
    if (!bullets.length) {
      bullets.push('Une signature qui correspond à vos réponses.');
    }
    return bullets;
  }

  function renderBonuses(entries) {
    var row = root.querySelector('[data-quiz-bonus-row]');
    if (!row) return;
    row.innerHTML = '';
    if (!cardPool) return;
    var selectedCount = (u.noteTokens || []).length;
    entries.forEach(function (entry) {
      var p = entry.p || entry; // tolerate either {p,matches} or bare product
      var tpl = cardPool.querySelector('template[data-product-id="' + p.id + '"]');
      if (!tpl || !tpl.content) return;

      // Wrap each card so we can overlay a small match badge in the
      // top-left corner without touching the shared scent-card snippet.
      var wrap = document.createElement('div');
      wrap.className = 'scent-quiz__bonus-cell';
      wrap.appendChild(tpl.content.cloneNode(true));

      if (selectedCount > 0) {
        var matches = entry.matches || 0;
        var badge = document.createElement('span');
        badge.className = 'scent-quiz__bonus-match' + (matches === 0 ? ' is-warn' : '');
        badge.setAttribute('aria-label',
          matches + ' sur ' + selectedCount + ' notes choisies présentes');
        badge.textContent = matches + ' / ' + selectedCount + ' notes';
        wrap.appendChild(badge);
      }

      row.appendChild(wrap);
    });
  }

  // ---------- ATC (mirrors assets/scent-card.js pattern) ----------
  function findCart() {
    return document.querySelector('cart-notification') ||
           document.querySelector('cart-drawer');
  }
  function getSectionsToRender() {
    var cart = findCart();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      return cart.getSectionsToRender().map(function (s) { return s.id; });
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }
  function flash(el, cls, ms) {
    el.classList.add(cls);
    window.setTimeout(function () { el.classList.remove(cls); }, ms || 1400);
  }

  function addToCart(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.classList.remove('is-success', 'is-error');
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    var cart = findCart();
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('sections', getSectionsToRender().join(','));
    formData.append('sections_url', window.location.pathname);

    var endpoint = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    fetch(endpoint, {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }
    })
      .then(function (r) { return r.json().then(function (json) { return { ok: r.ok, body: json }; }); })
      .then(function (res) {
        if (!res.ok || res.body.status) {
          flash(btn, 'is-error', 1800);
          return;
        }
        flash(btn, 'is-success', 1400);
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(res.body);
        }
      })
      .catch(function () { flash(btn, 'is-error', 1800); })
      .finally(function () {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      });
  }

  // ---------- helpers ----------
  function setText(scope, sel, txt) {
    var el = scope.querySelector(sel); if (el) el.textContent = txt;
  }
  function setHref(scope, sel, href) {
    var el = scope.querySelector(sel); if (el && href) el.setAttribute('href', href);
  }
  function capitalize(s) {
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  // ---------- click delegation ----------
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-quiz-start]')) {
      e.preventDefault();
      show('1');
      return;
    }

    if (e.target.closest('[data-quiz-restart]')) {
      e.preventDefault();
      u = freshAxes();
      axisHistory = {};
      clearMultiSelect();
      var hero = root.querySelector('[data-quiz-hero]');
      if (hero) hero.hidden = true;
      show('0');
      return;
    }

    var backBtn = e.target.closest('[data-quiz-back]');
    if (backBtn) {
      e.preventDefault();
      var current = currentStepName();
      var prev = String(parseInt(current, 10) - 1);
      // Q1 → intro
      if (current === '1') {
        show('0');
        return;
      }
      // Revert any commit recorded for this step (in case user advanced
      // forward, came back, and is now leaving without re-answering).
      if (axisHistory[current]) {
        revertAxes(axisHistory[current]);
        axisHistory[current] = null;
      }
      // Q6-specific: also reset note pills + noteAffinity + noteTokens so
      // re-entering Q6 starts clean if the user wants to change their picks.
      if (current === '6') {
        clearMultiSelect();
        u.noteAffinity = {};
        u.noteTokens = [];
      }
      show(prev);
      return;
    }

    var pill = e.target.closest('[data-quiz-note-toggle]');
    if (pill) {
      e.preventDefault();
      togglePill(pill);
      return;
    }

    var multiNext = e.target.closest('[data-quiz-next-from-multi]');
    if (multiNext) {
      e.preventDefault();
      var thisStep = currentStepName();
      // Revert any prior commit on this step (in case of re-answer)
      if (axisHistory[thisStep]) revertAxes(axisHistory[thisStep]);
      // Reset note state collected from this step before re-collecting
      u.noteAffinity = {};
      u.noteTokens = [];
      var snap = commitMultiSelect();
      axisHistory[thisStep] = snap;
      var next = multiNext.dataset.next;
      if (next) show(next);
      return;
    }

    var ans = e.target.closest('[data-quiz-answer]');
    if (ans) {
      e.preventDefault();
      var step = currentStepName();
      // If user re-answers same step, revert previous before applying new
      if (axisHistory[step]) revertAxes(axisHistory[step]);
      var raw = ans.getAttribute('data-axes') || '{}';
      var d = {};
      try { d = JSON.parse(raw); } catch (e2) {}
      var snap2 = applyAxes(d);
      axisHistory[step] = snap2;
      var nxt = ans.dataset.next;
      if (nxt === 'result' || !nxt) {
        computeAndReveal();
      } else {
        show(nxt);
      }
      return;
    }

    var atc = e.target.closest('[data-hero-atc]');
    if (atc) {
      e.preventDefault();
      addToCart(atc);
      return;
    }
  });

  // Init: ensure progressbar reflects current step (intro)
  updateProgressbar(currentStepName());
})();
